import { defineComponent, h } from 'vue';
import { QCard, QCardSection, QCardActions, QBtn, QIcon, QTabPanels, QTabPanel } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import cloneDeep from 'lodash.clonedeep';
import utils from '../../Shared/utils.jsx';

const WizardStep = defineComponent({
    name: 'wizard-step',
    props: {
        step: {},
        context: null,
    },
    data() {
        return {
            show: false,
        };
    },
    async mounted() {
        try {
            await utils.executeAndCompileAllActions(this.step.PrerenderActions, null, this.context);
        } catch (e) {
            utils.error(e);
        }
        this.show = true;
    },
    methods: {
        // Step body/footer controls are Vue children of wizard-step (not Wizard). Vue3
        // ChildControlByType walks Wizard._children filled only by finished-render; without
        // this wire ParentByType('Wizard').ChildControlByType('BasicForm') stays null
        // (general/forms/resetpassword). Vue2 used $children so the gap was invisible.
        registerStepControl(control) {
            const host = this.context;
            if (!control || !host || typeof host.finishRenderHandler !== 'function')
                return;
            host.finishRenderHandler(control);
            // $parent is wizard-step (no _children); tag host so BaseComponentMixin unmount cleans Wizard._children
            control._childTreeHost = host;
        },
    },
    render() {
        if (!this.show)
            return null;

        let controls = null;
        let footerControls = null;
        // Vue3 JSX listens via onFinishedRender; a 'finished-render' key is only a prop (AccordionGroup pattern is wrong for Vue3).
        const finishedRender = { onFinishedRender: (item) => this.registerStepControl(item) };

        if (this.step.Controls && this.step.Controls.length)
            controls = utils.generateItemsFromArray(h, this.context, this.step.Controls, null, 'VerticalLayout', finishedRender);
        if (this.step.FooterControls && this.step.FooterControls.length)
            footerControls = utils.generateItemsFromArray(h, this.context, this.step.FooterControls, null, 'HorizontalLayout', finishedRender);

        return (
            <div class="column fit">
                <QCardSection class="q-pb-none">
                    {controls}
                </QCardSection>
                <QCardActions class="q-mt-auto">
                    {footerControls}
                </QCardActions>
            </div>
        );
    },
});

export default defineComponent({
    name: 'cc-wizard',
    mixins: [BaseComponent],
    components: {
        QCard,
        QCardSection,
        QCardActions,
        QBtn,
        QIcon,
        QTabPanels,
        QTabPanel,
        WizardStep,
    },
    data() {
        return {
            titleEval: null,
            initialStepEval: null,
            currentStep: null,
            showBreadCrumbEval: null,
            colorEval: null,
            breadCrumbs: [],
            localSteps: [],
            currentStepIndex: 0,
        };
    },
    computed: {
        CurrentStepName() {
            if (this.currentStep)
                return this.currentStep.Name;
            return '';
        },
        CurrentStep() {
            return this.currentStep;
        },
        styles() {
            return {
                ...utils.resolveStyleHints(this.styleHints, this),
                ...this.sizeStyle,
            };
        },
        title() {
            try {
                if (!this.titleEval && this.controlData.Title)
                    this.titleEval = utils.compile(this, this.controlData.Title);
                if (this.titleEval)
                    return utils.evaluate(this.titleEval, this);
                return '';
            } catch (e) {
                utils.warn('Title could not evaluate expression: ' + this.controlData.Title + '; ' + e);
                return '';
            }
        },
        initialStep() {
            try {
                if (this.initialStepEval === null && this.controlData.InitialStep)
                    this.initialStepEval = utils.compile(this, this.controlData.InitialStep);
                if (this.initialStepEval)
                    return utils.evaluate(this.initialStepEval, this);
                return '';
            } catch (e) {
                utils.warn('InitialStep could not evaluate expression: ' + this.controlData.InitialStep + '; ' + e);
                return '';
            }
        },
        showBreadCrumb() {
            try {
                if (this.showBreadCrumbEval === null && this.controlData.ShowBreadCrumb)
                    this.showBreadCrumbEval = utils.compile(this, this.controlData.ShowBreadCrumb);
                if (this.showBreadCrumbEval)
                    return utils.evaluate(this.showBreadCrumbEval, this);
                return false;
            } catch (e) {
                utils.warn('ShowBreadCrumb could not evaluate expression: ' + this.controlData.ShowBreadCrumb + '; ' + e);
                return false;
            }
        },
        color() {
            try {
                if (this.colorEval === null && this.controlData.Color)
                    this.colorEval = utils.compile(this, this.controlData.Color);
                if (this.colorEval)
                    return utils.evaluate(this.colorEval, this);
                return '';
            } catch (e) {
                utils.warn('Color could not evaluate expression: ' + this.controlData.Color + '; ' + e);
                return '';
            }
        },
        steps() {
            try {
                if (this.controlData.Steps && this.controlData.Steps.length) {
                    if (!this.localSteps.length)
                        this.localSteps = cloneDeep(this.controlData.Steps);

                    for (let step of this.localSteps) {
                        if (step.Name && !step.NameCompiled)
                            step.NameCompiled = utils.compile(this, this.Translate(step.Name));
                        if (step.Title && !step.TitleCompiled)
                            step.TitleCompiled = utils.compile(this, this.Translate(step.Title));

                        if (step.NameCompiled)
                            step.Name = utils.evaluate(step.NameCompiled, this);
                        if (step.TitleCompiled)
                            step.Title = utils.evaluate(step.TitleCompiled, this);
                    }

                    return this.localSteps;
                }
                return [];
            } catch (e) {
                utils.warn('Steps could not evaluate expression: ' + this.controlData.Steps + '; ' + e);
                return [];
            }
        },
    },
    methods: {
        ClearBreadcrumbs() {
            this.clearBreadCrumbs();
        },
        GotoStepByName(stepName) {
            this.changeStepByName(stepName);
        },
        NextStep() {
            this.nextStep();
        },
        PreviousStep() {
            this.previousStep();
        },
        preRenderComplete() {
            this.setInitialStep();
            this.finishRenderHandler(this);
        },
        setInitialStep() {
            if (this.controlData.Steps && this.controlData.Steps.length) {
                if (this.initialStep)
                    this.changeStepByName(this.initialStep);
                else
                    this.changeStep(this.steps[0], 0);
            }
        },
        changeStep(step, index) {
            this.currentStepIndex = index;
            this.currentStep = step;
            step.index = index;
            this.addBreadCrumb(step);
        },
        addBreadCrumb(step) {
            this.breadCrumbs.push(step);
        },
        changeStepByName(stepName) {
            for (let i in this.steps) {
                let step = this.steps[i];
                if (step.Name === stepName) {
                    this.changeStep(step, parseInt(i, 10));
                    return;
                }
            }
        },
        nextStep() {
            let nextStepIndex = this.currentStep.index + 1;
            if (nextStepIndex < this.steps.length)
                this.changeStep(this.steps[nextStepIndex], nextStepIndex);
        },
        previousStep() {
            let index = this.breadCrumbs.length - 2;
            this.breadCrumbNavigate(this.breadCrumbs[index], index);
        },
        breadCrumbNavigate(breadCrumb, index) {
            this.breadCrumbs.splice(index, this.breadCrumbs.length - index);
            this.changeStep(breadCrumb, breadCrumb.index);
        },
        clearBreadCrumbs() {
            this.breadCrumbs = [this.currentStep];
        },
        generateBreadCrumbs() {
            if (this.breadCrumbs && this.breadCrumbs.length) {
                const crumbs = [];
                for (let i = 0; i < this.breadCrumbs.length; i++) {
                    const crumb = this.breadCrumbs[i];
                    crumbs.push(
                        <QBtn
                            flat
                            dense
                            no-caps
                            disable={i >= this.breadCrumbs.length - 1}
                            onClick={() => this.breadCrumbNavigate(crumb, i)}
                        >
                            {crumb.Title || crumb.Name}
                        </QBtn>
                    );
                    if (i < this.breadCrumbs.length - 1)
                        crumbs.push(<QIcon size="16px" name="mdi-chevron-right"></QIcon>);
                }
                return <div class="row items-center no-wrap">{crumbs}</div>;
            }
            return null;
        },
        generateSteps() {
            const items = [];
            for (let i = 0; i < this.steps.length; i++) {
                const step = this.steps[i];
                items.push(
                    <QTabPanel key={i} name={i} class="q-pa-none">
                        <WizardStep step={step} context={this}></WizardStep>
                    </QTabPanel>
                );
            }
            return items;
        },
    },
    render() {
        try {
            if (!this.todisplay)
                return null;

            const crumbs = this.generateBreadCrumbs();
            const items = this.generateSteps();

            return (
                <QCard
                    class={{ 'c-Wizard column': true, [`c-name-${this.name || 'unnamed'}`]: true, 'q-ma-xs': true }}
                    v-show={this.isvisible}
                    style={this.styles}
                    flat
                >
                    <QCardSection class="text-h4 text-white q-py-sm" style={{ backgroundColor: 'var(--v-navigation-base)' }}>
                        {this.title}
                    </QCardSection>

                    <QCardSection class="q-pb-none">
                        {crumbs}
                    </QCardSection>

                    <QTabPanels modelValue={this.currentStepIndex} animated class="col q-pa-none">
                        {items}
                    </QTabPanels>
                </QCard>
            );
        } catch (e) {
            utils.error('Wizard Render failed', e);
            return <div>Wizard Failed to Render {e}</div>;
        }
    },
});